<template>
  <div>
    <title-bar :title-stack="titleStack" />

    <div class="page-toolbar">
      <div class="page-toolbar-right">
        <b-button icon-left="file-plus" type="is-primary" size="is-small" @click="handleAddSale">
          Inregistrare noua
        </b-button>

        <b-button icon-left="refresh" type="is-light" size="is-small" @click="loadSaleOffers" title="Reincarcare date" />

        <download-excel :data="offers" :fields="json_fields" name="Registru_vanzari_terenuri.xls" worksheet="Vanzari" style="display:inline-flex">
          <b-button icon-left="file-excel" type="is-light" size="is-small" :disabled="!offers.length" title="Export Excel" />
        </download-excel>

        <b-button
          icon-left="calendar-star"
          size="is-small"
          :type="holidaysOpen ? 'is-info' : 'is-light'"
          :title="`Zile libere ${currentYear}`"
          @click="holidaysOpen = !holidaysOpen"
        >
          {{ currentYearHolidayCount }}
        </b-button>
      </div>
    </div>

    <section class="section is-main-section">
      <!-- Zile libere panel (collapsible) -->
      <transition name="panel-slide">
        <div v-if="holidaysOpen" class="holidays-panel mb-4">
          <div class="holidays-panel-body">
            <div class="columns">
              <div class="column is-4">
                <b-field label="Adauga zi libera">
                  <b-datepicker v-model="newHoliday" locale="ro" placeholder="Selecteaza data" />
                </b-field>
              </div>
              <div class="column is-2" style="display:flex;align-items:flex-end;padding-bottom:0.75rem">
                <b-button type="is-primary" @click="addHoliday" :disabled="!newHoliday">Adauga</b-button>
              </div>
            </div>
            <b-tabs v-model="activeHolidayTab" type="is-toggle">
              <b-tab-item
                v-for="year in holidayYears"
                :key="year"
                :label="`${year} (${holidaysByYear[year].length})`"
              >
                <div class="field is-grouped is-grouped-multiline">
                  <div class="control" v-for="h in holidaysByYear[year]" :key="h.raw">
                    <b-tag type="is-info" closable @close="removeHoliday(h.raw)">{{ h.display }}</b-tag>
                  </div>
                </div>
              </b-tab-item>
            </b-tabs>
          </div>
        </div>
      </transition>

      <card-component
        class="has-table has-mobile-sort-spaced"
        title="Inregistrari"
        icon="file-multiple"
      >
        <sales-table
          :offers="offers"
          :isLoading="isLoading"
          @handleDeleteSale="handleDeleteSale"
          @search="onSearch"
        />
      </card-component>
    </section>
  </div>
</template>

<script>
import moment from "moment";
import ElectronicRegisterSalesOffer from "@/services/electronicRegisterSalesOffer.service";
import { notify } from "@/helpers/notify";
import { getHolidays, HOLIDAY_FORMAT } from "@/config/holidays";
const _ = require("lodash");

const HOLIDAYS_STORAGE_KEY = "sales_holidays";

function loadHolidays() {
  try {
    const stored = localStorage.getItem(HOLIDAYS_STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch (e) {
    // ignore corrupt data
  }
  return getHolidays(new Date().getFullYear(), 2028);
}

import SalesTable from "@/components/sales/sales-table";
import CardComponent from "@/components/CardComponent";
import TitleBar from "@/components/TitleBar";
export default {
  name: "Sales",
  components: {
    TitleBar,
    CardComponent,
    SalesTable,
  },
  data() {
    return {
      isLoading: true,
      allOffers: [],
      offers: [],
      searchFilters: {},
      json_fields: {
        "Nr. Crt.": "nrCrt",
        "Vanzator - nume si prenume": "sellerFirstNameLastName",
        "Vanzator - adresa": "sellerAddress",
        "Suprafata teren (mp)": "surface",
        "Nr. carte funciara": "landRegistryNumber",
        "Categorie folosinta": "category",
        "Pret de vanzare": "price",
        "Tarla": "tarla",
        "Parcela": "parcela",
        "Cumparator": "buyer",
        "Data inregistrarii": {
          callback: (value) =>
            value.registrationDate
              ? moment(value.registrationDate).format("DD/MM/YYYY")
              : "",
        },
        "PV Procedura": {
          callback: (value) =>
            value.procesVerbalProcedura
              ? moment(value.procesVerbalProcedura).format("DD/MM/YYYY")
              : "",
        },
        "PV Finalizare": {
          callback: (value) =>
            value.procesVerbalFinalizare
              ? moment(value.procesVerbalFinalizare).format("DD/MM/YYYY")
              : "",
        },
      },
      activeHolidayTab: 0,
      newHoliday: null,
      holidays: loadHolidays(),
      holidaysOpen: false,
    };
  },
  computed: {
    titleStack() {
      return ["Registru Evidenta Vanzare Terenuri Agricole", "Vanzari"];
    },
    heroRouterLinkTo() {
      return { name: "/sale/add" };
    },
    holidaysByYear() {
      const grouped = {};
      for (const h of this.holidays) {
        const parts = h.split("-");
        const year = parts[2];
        if (!grouped[year]) grouped[year] = [];
        grouped[year].push({
          raw: h,
          display: h,
          date: new Date(parts[2], parts[1] - 1, parts[0]),
        });
      }
      for (const year of Object.keys(grouped)) {
        grouped[year].sort((a, b) => a.date - b.date);
      }
      return grouped;
    },
    holidayYears() {
      const current = new Date().getFullYear();
      return Object.keys(this.holidaysByYear)
        .filter((y) => Number(y) >= current)
        .sort();
    },
    currentYear() {
      return String(new Date().getFullYear());
    },
    currentYearHolidayCount() {
      return (this.holidaysByYear[this.currentYear] || []).length;
    },
  },
  created() {
    this.loadSaleOffers();
    const yearIndex = this.holidayYears.indexOf(this.currentYear);
    if (yearIndex !== -1) {
      this.activeHolidayTab = yearIndex;
    }
  },
  methods: {
    loadSaleOffers() {
      this.isLoading = true;
      this.getCityHallSaleOffers()
        .then((data) => {
          const items = Array.isArray(data) ? data : data.items;
          const groups = this.groupBy(items, "year");
          const result = [];
          for (const group of groups) {
            group.sort((a, b) => a.id - b.id);
            group.forEach((item, idx) => {
              item.nrCrt = idx + 1;
              result.push(item);
            });
          }
          this.allOffers = _.orderBy(result, ["year", "nrCrt"], ["desc", "desc"]);
          this.applySearch();
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    applySearch() {
      const filters = this.searchFilters;
      const hasFilters = Object.values(filters).some((v) => v && v.trim());
      if (!hasFilters) {
        this.offers = this.allOffers;
        return;
      }
      this.offers = this.allOffers.filter((item) => {
        return Object.keys(filters).every((key) => {
          const term = filters[key];
          if (!term || !term.trim()) return true;
          const value = item[key];
          if (value == null) return false;
          return String(value).toLowerCase().includes(term.trim().toLowerCase());
        });
      });
    },
    groupBy(arr, prop) {
      const map = new Map(Array.from(arr, (obj) => [obj[prop], []]));
      arr.forEach((obj) => map.get(obj[prop]).push(obj));
      return Array.from(map.values());
    },
    async getCityHallSaleOffers() {
      const { handleGetAllSales } = ElectronicRegisterSalesOffer;
      const response = await handleGetAllSales();
      return response.data;
    },
    onSearch(searchTerms) {
      this.searchFilters = searchTerms;
      this.applySearch();
    },
    async handleAddSale() {
      this.$router.push({
        path: "/sales/add",
      });
    },
    async handleDeleteSale(id) {
      try {
        const { handleDeleteSale } = ElectronicRegisterSalesOffer;
        const response = await handleDeleteSale(id);
        const { data, status } = response;
        if (status == "200") {
          this.allOffers = this.allOffers.filter((prop) => prop.id !== id);
          this.applySearch();
          notify.success(this, "Inregistrarea a fost stearsa.");
        } else {
          notify.error(this, "Eroare la stergerea inregistrarii.");
        }
      } catch (error) {
        notify.error(this);
      }
    },
    addHoliday() {
      if (!this.newHoliday) return;
      const d = this.newHoliday;
      const formatted = `${String(d.getDate()).padStart(2, "0")}-${String(
        d.getMonth() + 1
      ).padStart(2, "0")}-${d.getFullYear()}`;
      if (this.holidays.includes(formatted)) {
        this.$buefy.toast.open({
          message: `Data ${formatted} exista deja in lista de zile libere.`,
          type: "is-warning",
        });
      } else {
        this.holidays.push(formatted);
        this.saveHolidays();
        this.$buefy.toast.open({
          message: `Zi libera adaugata: ${formatted}. Modificarea se aplica doar pentru calculele viitoare si nu afecteaza inregistrarile deja salvate.`,
          type: "is-success",
          duration: 5000,
        });
      }
      this.newHoliday = null;
    },
    removeHoliday(dateStr) {
      this.holidays = this.holidays.filter((h) => h !== dateStr);
      this.saveHolidays();
      this.$buefy.toast.open({
        message: `Zi libera stearsa: ${dateStr}. Modificarea se aplica doar pentru calculele viitoare si nu afecteaza inregistrarile deja salvate.`,
        type: "is-danger",
        duration: 5000,
      });
    },
    saveHolidays() {
      localStorage.setItem(HOLIDAYS_STORAGE_KEY, JSON.stringify(this.holidays));
    },
  },
};
</script>

<style scoped>
.page-toolbar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0.75rem 1.5rem;
  border-bottom: 1px solid #f0f0f0;
  background: #fff;
}

.page-toolbar-right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.holidays-panel {
  border: 1px solid #dbdbdb;
  border-left: 3px solid #3273dc;
  border-radius: 6px;
  background: #f5f7fb;
  overflow: hidden;
}

.holidays-panel-body {
  padding: 1rem 1.25rem;
}

.panel-slide-enter-active,
.panel-slide-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.panel-slide-enter,
.panel-slide-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
